import { useMemo, type PropsWithChildren } from 'react'
import { useIntl } from 'react-intl'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'
import HeadData from 'components/Common/HeadData'
import { LUXURY_ESCAPES } from 'constants/brands'
import config from 'constants/config'
import EvergreenPageSeoContext from 'contexts/EvergreenPageSeoContext'
import srOnly from 'styles/tools/sr-only'
import { auEvergreenPageHeadings, auEvergreenPageTitles } from './auEvergreenPageMessages'

// Keep the existing hero designs while exposing a stable page heading to
// assistive technology and in the first server-rendered HTML response.
const PageHeading = styled.h1`${srOnly}`

function AuEvergreenPage({ children }: PropsWithChildren) {
  const { pathname } = useLocation()
  const intl = useIntl()
  const seo = useMemo(() => {
    // Exact landing pages only: never affect translated URLs, subpages or
    // white labels. /au/app remains an existing redirect, not a landing page.
    if (config.BRAND !== LUXURY_ESCAPES || intl.locale !== 'en-AU') return
    const match = /^\/au\/([^/]+)\/?$/.exec(pathname)
    const slug = match?.[1]
    if (!slug || !Object.hasOwn(auEvergreenPageTitles, slug)) return
    const page = slug as keyof typeof auEvergreenPageTitles
    return {
      title: intl.formatMessage(auEvergreenPageTitles[page]),
      heading: intl.formatMessage(auEvergreenPageHeadings[page]),
    }
  }, [intl, pathname])

  // Always render the Provider so children keep their position in the tree;
  // switching between landing pages and subpages must not remount the page.
  return <EvergreenPageSeoContext.Provider value={seo}>
    {seo && <HeadData title={seo.title} />}
    {seo && <PageHeading>{seo.heading}</PageHeading>}
    {children}
  </EvergreenPageSeoContext.Provider>
}

export default AuEvergreenPage
